import type { components } from "../../api/generated-schema";
import { apiRequest, createRequestId } from "../../api/httpClient";

export type UserAccountAdminPage = components["schemas"]["UserAccountAdminPage"];
export type UserAccountAdminDetail = components["schemas"]["UserAccountAdminDetail"];
export type UserAccountAdminListItem = components["schemas"]["UserAccountAdminListItem"];
export type UserAccountPersonOptionPage = components["schemas"]["UserAccountPersonOptionPage"];
export type CreateUserAccountRequest = components["schemas"]["CreateUserAccountRequest"];
export type UserAccountLifecycleRequest = components["schemas"]["UserAccountLifecycleRequest"];
export type OrganizationSummary = components["schemas"]["OrganizationSummary"];
export type IdentityOperationAccepted = components["schemas"]["IdentityOperationAccepted"];

export type UserAccountStatus = "INACTIVE" | "ACTIVE" | "LOCKED";

export type LifecycleActionKind = "activate" | "lock" | "unlock";

/** S2.3.5 — hành động identity (IdP delivery) hiển thị trong SCR-051. */
export type IdentityActionKind = "provision" | "retry" | "synchronize" | "request-password-reset";

/** S2.3.5 — trạng thái delivery nghiệp vụ đã sanitize (không bao giờ là raw provider state). */
export type IdentityDeliveryStatus = NonNullable<UserAccountAdminDetail["identityDeliveryStatus"]>;

export type AccountCommandError = Readonly<{
  code: string;
  requestId?: string | undefined;
}>;

/**
 * Nguồn sự thật duy nhất cho ánh xạ trạng thái ↔ hành động vòng đời:
 * mỗi hành động công khai chỉ được phép từ ĐÚNG một trạng thái nguồn.
 */
export const LIFECYCLE_ACTION_SOURCE: Readonly<Record<LifecycleActionKind, UserAccountStatus>> = {
  activate: "INACTIVE",
  lock: "ACTIVE",
  unlock: "LOCKED",
};

/** Suy ra hành động hợp lệ duy nhất cho một trạng thái (không có ánh xạ thứ hai bằng tay). */
export function actionFor(status: UserAccountStatus): LifecycleActionKind {
  const entry = (Object.entries(LIFECYCLE_ACTION_SOURCE) as Array<[LifecycleActionKind, UserAccountStatus]>).find(
    ([, source]) => source === status,
  );
  if (!entry) {
    throw new Error(`Trạng thái tài khoản không hợp lệ: ${status}`);
  }
  return entry[0];
}

export function lifecycleSuccessMessage(action: LifecycleActionKind): string {
  switch (action) {
    case "activate":
      return "Kích hoạt thành công";
    case "lock":
      return "Khóa tài khoản thành công";
    case "unlock":
      return "Mở khóa thành công";
  }
}

const BASE_PATH = "/admin/user-accounts";

function commandHeaders(idempotencyKey: string): Record<string, string> {
  return { "Idempotency-Key": idempotencyKey, "X-Request-Id": createRequestId() };
}

function signalInit(signal?: AbortSignal): RequestInit {
  return signal ? { signal } : {};
}

function postCommand<T>(path: string, body: unknown, idempotencyKey: string): Promise<T> {
  return apiRequest<T>(path, {
    method: "POST",
    headers: commandHeaders(idempotencyKey),
    body: JSON.stringify(body),
  });
}

function encodeQuery(entries: Array<[string, string]>): string {
  return entries.map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join("&");
}

export function listUserAccounts(
  filters: {
    page: number;
    size: number;
    q?: string;
    status?: string;
    organizationId?: string;
  },
  signal?: AbortSignal,
): Promise<UserAccountAdminPage> {
  const entries: Array<[string, string]> = [
    ["page", String(filters.page)],
    ["size", String(filters.size)],
  ];
  if (filters.q) entries.push(["q", filters.q]);
  if (filters.status) entries.push(["status", filters.status]);
  if (filters.organizationId) entries.push(["organizationId", filters.organizationId]);
  return apiRequest<UserAccountAdminPage>(`${BASE_PATH}?${encodeQuery(entries)}`, signalInit(signal));
}

export function getUserAccount(id: string, signal?: AbortSignal): Promise<UserAccountAdminDetail> {
  return apiRequest<UserAccountAdminDetail>(
    `${BASE_PATH}/${encodeURIComponent(id)}`,
    signalInit(signal),
  );
}

export function searchUserAccountPersonOptions(
  query: string,
  page: number,
  size: number,
  signal?: AbortSignal,
): Promise<UserAccountPersonOptionPage> {
  const queryString = encodeQuery([
    ["q", query.trim()],
    ["page", String(page)],
    ["size", String(size)],
  ]);
  return apiRequest<UserAccountPersonOptionPage>(
    `${BASE_PATH}/person-options?${queryString}`,
    signalInit(signal),
  );
}

export function createUserAccount(
  body: CreateUserAccountRequest,
  idempotencyKey: string,
): Promise<UserAccountAdminDetail> {
  return postCommand<UserAccountAdminDetail>(BASE_PATH, body, idempotencyKey);
}

export function activateUserAccount(
  id: string,
  body: { expectedVersion: number },
  idempotencyKey: string,
): Promise<UserAccountAdminDetail> {
  return postCommand<UserAccountAdminDetail>(
    `${BASE_PATH}/${encodeURIComponent(id)}/activate`,
    body,
    idempotencyKey,
  );
}

export function lockUserAccount(
  id: string,
  body: UserAccountLifecycleRequest,
  idempotencyKey: string,
): Promise<UserAccountAdminDetail> {
  return postCommand<UserAccountAdminDetail>(
    `${BASE_PATH}/${encodeURIComponent(id)}/lock`,
    body,
    idempotencyKey,
  );
}

export function unlockUserAccount(
  id: string,
  body: UserAccountLifecycleRequest,
  idempotencyKey: string,
): Promise<UserAccountAdminDetail> {
  return postCommand<UserAccountAdminDetail>(
    `${BASE_PATH}/${encodeURIComponent(id)}/unlock`,
    body,
    idempotencyKey,
  );
}

// ---------------------------------------------------------------------------
// S2.3.5 — IdP delivery commands (CR-S2-3-5-001). Mọi lệnh đều bất đồng bộ:
// backend ghi intent rồi trả 202; UI chỉ được nói "đã tiếp nhận", không bao giờ
// tuyên bố đã gửi/đã xong. Provision mang expectedVersion từ detail đang tải;
// hai command còn lại KHÔNG nhận body (email/redirect/lifespan là server-side).
// ---------------------------------------------------------------------------

export function requestIdpProvisioning(
  id: string,
  body: { expectedVersion: number },
  idempotencyKey: string,
): Promise<IdentityOperationAccepted> {
  return postCommand<IdentityOperationAccepted>(
    `${BASE_PATH}/${encodeURIComponent(id)}/identity/provision`,
    body,
    idempotencyKey,
  );
}

export function requestIdpProfileSynchronization(
  id: string,
  idempotencyKey: string,
): Promise<IdentityOperationAccepted> {
  return postCommand<IdentityOperationAccepted>(
    `${BASE_PATH}/${encodeURIComponent(id)}/identity/synchronize-profile`,
    undefined,
    idempotencyKey,
  );
}

export function requestPasswordResetEmail(
  id: string,
  idempotencyKey: string,
): Promise<IdentityOperationAccepted> {
  return postCommand<IdentityOperationAccepted>(
    `${BASE_PATH}/${encodeURIComponent(id)}/password-reset-email`,
    undefined,
    idempotencyKey,
  );
}

/** §12 — nhãn nghiệp vụ; raw enum không bao giờ xuất hiện trên UI. */
export const IDENTITY_STATUS_LABEL: Readonly<Record<IdentityDeliveryStatus, string>> = {
  NOT_PROVISIONED: "Chưa thiết lập",
  PROCESSING: "Đang xử lý",
  READY: "Đã sẵn sàng",
  ACTION_REQUIRED: "Cần xử lý",
};

/** Badge AntD (dot + text) — trạng thái không chỉ truyền đạt bằng màu. */
export const IDENTITY_BADGE_STATUS: Readonly<
  Record<IdentityDeliveryStatus, "default" | "processing" | "success" | "warning">
> = {
  NOT_PROVISIONED: "default",
  PROCESSING: "processing",
  READY: "success",
  ACTION_REQUIRED: "warning",
};

/**
 * §13.1 — các boolean khả dụng tính TRỰC TIẾP từ primitive của detail (không
 * copy vào effect-driven state). Quyền tác vụ do backend enforce; visibility
 * frontend chỉ là UX hint theo đúng convention gate lifecycle của S2.3.4.
 * payload thiếu field identity (binary-compat S2.3.4) → fail-closed, không action.
 */
export function canProvisionIdentity(detail: UserAccountAdminDetail): boolean {
  return detail.status === "INACTIVE" && detail.identityDeliveryStatus === "NOT_PROVISIONED";
}

export function canRetryIdentity(detail: UserAccountAdminDetail): boolean {
  return detail.identityDeliveryStatus === "ACTION_REQUIRED";
}

export function canSynchronizeIdentity(detail: UserAccountAdminDetail): boolean {
  return (
    detail.profileSynchronizationAvailable === true &&
    (detail.identityDeliveryStatus === "READY" || detail.identityDeliveryStatus === "ACTION_REQUIRED")
  );
}

export function canRequestPasswordReset(detail: UserAccountAdminDetail): boolean {
  return (
    detail.status === "ACTIVE" &&
    detail.identityDeliveryStatus === "READY" &&
    detail.passwordResetAvailable === true
  );
}

/** Cảnh báo PROGRESSIVE "Chưa có email đã xác minh" — chỉ khi server nói rõ không available. */
export function needsVerifiedEmailWarning(detail: UserAccountAdminDetail): boolean {
  return (
    detail.status === "ACTIVE" &&
    detail.identityDeliveryStatus === "READY" &&
    detail.passwordResetAvailable === false
  );
}

/** §13.2 VERBATIM — copy giải thích boundary IdP trong modal xác nhận reset. */
export const PASSWORD_RESET_CONFIRM_TEXT =
  "Hệ thống sẽ yêu cầu dịch vụ đăng nhập gửi email để người dùng tự đặt mật khẩu mới. Quản trị viên không xem hoặc đặt mật khẩu.";

/** §12 — toast sau 202 chỉ xác nhận tiếp nhận; retry dùng cùng command provision nên chung copy. */
export function identityAcceptedMessage(kind: IdentityActionKind): string {
  switch (kind) {
    case "provision":
    case "retry":
      return "Đã tiếp nhận yêu cầu thiết lập đăng nhập.";
    case "synchronize":
      return "Đã tiếp nhận yêu cầu đồng bộ thông tin đăng nhập.";
    case "request-password-reset":
      return "Đã tiếp nhận yêu cầu gửi email đặt lại mật khẩu.";
  }
}

/** §14 — polling PROCESSING chỉ chạy khi Drawer mở; dừng khi đóng hoặc terminal. */
export const IDENTITY_POLL_INTERVAL_MS = 5_000;
